import { useEffect, useMemo, useRef, useState } from 'react'
import {
  X, Ticket as TicketIcon, Loader2, Trash2, Search,
  CheckCircle2, Circle, MessageSquarePlus, CalendarClock, Stethoscope, Send,
  Pencil, Check,
} from 'lucide-react'
import { apiGet, postJson, patchJson, cachedGet } from '../../lib/api.js'
import { nameInitial } from '../../lib/text.js'
import { useTypeahead } from '../../lib/useTypeahead.js'
import { useMe } from '../../lib/me.js'
import {
  Banner, Button, ConfirmDialog, Drawer, DrawerFooter, ErrorState, Field, FIELD_CLASS, IconButton, Input,
  Loading, PatientLink, Textarea, fmtDate, useToast,
} from '../../kit'
import {
  tId, tDetails, tDone, tType, tAppointmentId, tTreatmentId, tTags,
  tPatientId, tPatientName, tHN, tFollowUps, fuId, fuDetails, fuDate, fuCreator,
  fuCreatorId, fuEditedAt,
  tCreatedAt, tDateFollowup, tAgeDays, tPatientStatus,
  tAppointment, tTreatment, docName, docImg, fmtTimeBkk, dateOf, daysBetween,
  fmtDuration, todayBkk, addDays, addMonths,
} from '../../lib/ticket.js'
import { apptStatusInfo } from '../../data/appointmentStatuses.js'
import { STATUS_TONES, statusTone } from '../../data/patientStatuses.js'
import TagPicker from './TagPicker.jsx'
import { MIN_PATIENT_SEARCH } from '../../lib/patientSearch.js'

/*
 * TicketDrawer — right-side drawer for viewing/creating/editing a ticket + its
 * follow-up thread. Built on the kit Drawer (Esc / backdrop / ✕, busy + dirty guards,
 * the record's audit line pinned bottom-left).
 *
 * ⚠️ WRITE CONTRACTS ARE UNVERIFIED (Phase 0). Every payload built here is a
 * best-guess based on the OpenAPI spec + this workspace's conventions. They are
 * centralised in buildCreatePayload / buildEditPayload / the done-toggle / the
 * follow-up call below so that, once Phase 0 pins the real field names, this is a
 * one-spot fix. See docs/ticket-endpoint-notes.md.
 *
 * Props:
 *   mode: 'view' | 'create'   (view opens an existing ticket by id; create is blank)
 *   ticketId: number | null
 *   branches: [{ id, name }]  (already relabelled by the parent; id→name lookup only)
 *   tagCatalog: string[]
 *   onClose(), onSaved()      (onSaved → parent refetches the list)
 */

// Quick วันติดตาม choices, all measured from TODAY (not from the current value) —
// rescheduling an overdue ticket by "+7 วัน" means 7 days from now, not 7 days from
// the date it already blew past. '+1 เดือน' is exactly the max allowed.
const PRESETS = [
  { label: 'พรุ่งนี้', iso: (t) => addDays(t, 1) },
  { label: '+7 วัน', iso: (t) => addDays(t, 7) },
  { label: '+15 วัน', iso: (t) => addDays(t, 15) },
  { label: '+1 เดือน', iso: (t) => addMonths(t, 1) },
]

// ── write payloads ────────────────────────────────────────────────────────────
// Verified live 2026-07-16: POST and PATCH both accept `date_followup` (yyyy-mm-dd),
// and PATCH is pick-and-patch — fields left out of the body are preserved, so
// omitting follow_up[] does NOT wipe the notes thread.
// No branch_id: a ticket row has no such field (the branch comes from the joined
// appointment), so sending one was a silent no-op.
function buildCreatePayload(form) {
  return {
    details: form.details,
    patient_id: form.patientId || 0,
    tag: form.tags,                 // array; encoding per Xano
    type: form.type || undefined,
    date_followup: form.dateFollowup || undefined,
    done: false,
  }
}
function buildEditPayload(form) {
  return {
    details: form.details,
    patient_id: form.patientId || 0,
    tag: form.tags,
    type: form.type || undefined,
    date_followup: form.dateFollowup || undefined,
  }
}

// `initialPatient` ({id,name,hn}) seeds CREATE mode from a context that already
// knows the patient — the patient ติดตาม page. It is still editable via the same
// picker; omitting it keeps the standalone /follow-ups/ticket behaviour unchanged.
export default function TicketDrawer({ mode: initialMode, ticketId, branches = [], tagCatalog = [], initialPatient = null, onClose, onSaved }) {
  // branches is only a lookup table here (id → name) for the appointment card —
  // the ticket itself has no branch to edit.
  const branchName = (id) => branches.find((b) => String(b.id) === String(id))?.name || (id != null && id !== '' ? `สาขา #${id}` : '—')

  const isCreate = initialMode === 'create'
  const [loading, setLoading] = useState(!isCreate)
  const [row, setRow] = useState(null)
  const [err, setErr] = useState('')
  const [apptTypes, setApptTypes] = useState(new Map())  // appointmentlist_id → { type, detail }
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const toast = useToast()

  // editable form state
  const [details, setDetails] = useState('')
  const [type, setType] = useState('')
  const [tags, setTags] = useState([])
  const [patient, setPatient] = useState(initialPatient)  // { id, name, hn }
  const [changingPatient, setChangingPatient] = useState(false)
  // a new ticket starts out due the day it was opened
  const [dateFollowup, setDateFollowup] = useState(todayBkk())
  // the value as loaded, so an already-overdue ticket keeps its past date until
  // someone actually edits it — the min-date rule only applies to a manual change
  const [savedFollowup, setSavedFollowup] = useState('')

  const busy = saving || loading

  const today = todayBkk()
  const maxFollowup = addMonths(today, 1)          // ตั้งล่วงหน้าได้ไม่เกิน 1 เดือน
  const minFollowup = savedFollowup && savedFollowup < today ? savedFollowup : today

  // ── load an existing ticket (through the list endpoint, ?id=) ────────────────
  const load = () => {
    if (isCreate || !ticketId) return
    setLoading(true); setErr('')
    apiGet('/api/ticket', { id: ticketId, per_page: 1 })
      .then((r) => {
        const item = Array.isArray(r) ? r[0] : (r?.items?.[0] ?? r)
        if (!item || tId(item) == null) throw new Error('ไม่พบ ticket')
        hydrate(item)
      })
      .catch((e) => setErr(String(e.message || e)))
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() /* eslint-disable-next-line */ }, [ticketId, isCreate])

  // appointment-type catalog (id → type/detail); cached across drawer opens.
  // Only needed to label an existing ticket's appointment.
  useEffect(() => {
    if (isCreate) return
    cachedGet('/api/appointment-types')
      .then((r) => {
        const items = Array.isArray(r) ? r : (r?.items || [])
        setApptTypes(new Map(items.map((x) => [String(x.id), { type: x.type || '', detail: x.detail || '' }])))
      })
      .catch(() => {})
  }, [isCreate])

  function hydrate(item) {
    setRow(item)
    setDetails(tDetails(item))
    setType(tType(item))
    setTags(tTags(item))
    const df = tDateFollowup(item) || tCreatedAt(item)
    setDateFollowup(df)
    setSavedFollowup(df)
    const pid = tPatientId(item)
    setPatient(pid ? { id: pid, name: tPatientName(item), hn: tHN(item) } : null)
  }

  const formState = { details: details.trim(), type, tags, patientId: patient?.id || 0, dateFollowup }

  // Only a date the user actually touched is range-checked: tickets that are
  // already overdue must stay saveable without silently jumping to today.
  function followupError() {
    if (!dateFollowup || dateFollowup === savedFollowup) return ''
    if (dateFollowup < today) return 'วันติดตามต้องไม่ย้อนหลัง — เลือกวันที่ตั้งแต่วันนี้เป็นต้นไป'
    if (dateFollowup > maxFollowup) return `ตั้งวันติดตามล่วงหน้าได้ไม่เกิน 1 เดือน (ถึง ${fmtDate(maxFollowup)})`
    return ''
  }

  // The ONE write path. `setDone` is what the footer's เสร็จแล้ว / ยังไม่เสร็จ button
  // passes: closing a ticket saves the pending edits in the same PATCH, so a status
  // change can never silently discard what the user typed. (The old design wrote
  // `{done}` on its own, so editing รายละเอียด then clicking done lost the text.)
  async function save({ setDone } = {}) {
    if (!formState.details) { setErr('กรุณากรอกรายละเอียด'); return }
    const dfErr = followupError()
    if (dfErr) { setErr(dfErr); return }
    // closing the ticket is exempt — only "keep following up" needs a fresh date
    if (setDone !== true && mustReschedule) {
      setErr(`Ticket นี้เลยกำหนดติดตามมาแล้ว ${overdueDays} วัน — กรุณาตั้งวันติดตามใหม่ก่อนบันทึก หรือกด “เสร็จแล้ว” ถ้าจบงาน`)
      return
    }
    setSaving(true); setErr('')
    try {
      if (isCreate) {
        await postJson('/api/ticket', buildCreatePayload(formState))
      } else {
        const payload = buildEditPayload(formState)
        if (setDone !== undefined) payload.done = setDone
        await patchJson(`/api/ticket/${ticketId}`, payload)
      }
      toast.success(isCreate ? 'สร้าง Ticket แล้ว' : setDone === true ? 'ปิด Ticket แล้ว' : 'บันทึกแล้ว')
      onSaved?.()
      onClose?.()
    } catch (e) {
      setErr(String(e.message || e))
      toast.error('บันทึกไม่สำเร็จ', { detail: String(e.message || e) })
    } finally {
      setSaving(false)
    }
  }

  // The confirm stays up (busy = saving) while the DELETE runs, so a second click
  // cannot send it twice; it closes on failure so the error underneath is readable.
  async function doDelete() {
    setSaving(true); setErr('')
    try {
      const res = await fetch(`/api/ticket/${ticketId}`, { method: 'DELETE', credentials: 'include' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      toast.success('ลบ Ticket แล้ว')
      onSaved?.()
      onClose?.()
    } catch (e) {
      setErr(String(e.message || e))
      toast.error('ลบ Ticket ไม่สำเร็จ', { detail: String(e.message || e) })
      setConfirmDelete(false)
      setSaving(false)
    }
  }

  const done = tDone(row)
  const followUps = tFollowUps(row)
  const pStatus = tPatientStatus(row)
  const pTone = STATUS_TONES[statusTone(pStatus)] || STATUS_TONES.none

  // An open ticket past its วันติดตาม must be rescheduled before it can be saved —
  // otherwise it silently stays overdue and drops off nobody's radar. Marking it
  // เสร็จแล้ว is exempt: a closed ticket needs no future date.
  const overdueDays = savedFollowup && savedFollowup < today ? Math.abs(daysBetween(today, savedFollowup)) : 0
  const mustReschedule = !isCreate && !done && overdueDays > 0 && dateFollowup === savedFollowup

  // Picking a date clears any stale error — otherwise the "กรุณาตั้งวันติดตามใหม่"
  // message lingers after the user has already fixed it.
  const pickFollowup = (iso) => { setDateFollowup(iso); if (err) setErr('') }

  // The ticket's own tags must stay selectable even if the catalog lacks them.
  const tagOptions = useMemo(
    () => [...new Set([...tagCatalog, ...tags])].filter(Boolean),
    [tagCatalog, tags]
  )

  const appt = tAppointment(row)
  const treat = tTreatment(row)
  const apptTime = appt ? [fmtTimeBkk(appt.start_date), fmtTimeBkk(appt.end_date)].filter(Boolean).join('–') : ''
  // appointmentlist_id → "GP · ขูดหินปูน" via the cached type catalog
  const apptType = (() => {
    const hit = appt?.appointmentlist_id != null ? apptTypes.get(String(appt.appointmentlist_id)) : null
    return hit ? [hit.type, hit.detail].filter(Boolean).join(' · ') : ''
  })()

  // Unsaved edits → the Drawer asks "ทิ้งการแก้ไข?" before Esc / backdrop / ✕ lose them.
  const sameTags = (a, b) => JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort())
  const dirty = isCreate
    ? !!details.trim() || tags.length > 0 || type !== '' || (patient?.id || 0) !== (initialPatient?.id || 0) || dateFollowup !== today
    : !!row && (
      details !== tDetails(row) || type !== tType(row) || !sameTags(tags, tTags(row))
      || (patient?.id || 0) !== (tPatientId(row) || 0) || dateFollowup !== savedFollowup
    )
  // An existing ticket that failed to load has nothing to edit: show the error alone.
  const loadFailed = !isCreate && !loading && !row && !!err

  const statusLine = <>Ticket #{ticketId} · <span className={done ? 'text-emerald-600' : 'text-amber-600'}>{done ? 'เสร็จแล้ว' : 'รอดำเนินการ'}</span></>
  const firstLine = (String(tDetails(row) || details || '').split('\n').map((x) => x.trim()).find(Boolean) || '').slice(0, 80)

  // ── footer: ONE write path. เสร็จแล้ว saves the edits too. ───────────────────
  // ลบ sits far left (it always confirms); the status action sits beside it, apart from
  // the routine ยกเลิก/บันทึก pair — it changes what the ticket IS. Both go through save().
  const footer = loading ? null : (
    <DrawerFooter
      onCancel={() => !busy && onClose?.()}
      cancelLabel={loadFailed ? 'ปิด' : 'ยกเลิก'}
      onSave={loadFailed ? undefined : () => save()}
      saving={saving}
      canSave={!busy && !!details.trim()}
      saveLabel={isCreate ? 'สร้าง Ticket' : 'บันทึก'}
      status={!loadFailed && !details.trim() ? 'กรอกรายละเอียดก่อนบันทึก' : null}
      danger={!isCreate && !loadFailed ? (
        <>
          <IconButton icon={Trash2} label="ลบ ticket" variant="danger-soft" onClick={() => setConfirmDelete(true)} disabled={busy} />
          {done ? (
            <Button variant="secondary" icon={Circle} onClick={() => save({ setDone: false })} disabled={busy || !details.trim()} title="กลับมาเป็นยังไม่เสร็จ (บันทึกการแก้ไขด้วย)">
              ยังไม่เสร็จ
            </Button>
          ) : (
            <Button variant="soft" icon={CheckCircle2} onClick={() => save({ setDone: true })} disabled={busy || !details.trim()} title="ปิดงานนี้ (บันทึกการแก้ไขด้วย)">
              เสร็จแล้ว
            </Button>
          )}
        </>
      ) : null}
    />
  )

  const hasPatientHeader = !isCreate && !!patient
  return (
    <Drawer
      onClose={onClose}
      width="md"
      icon={TicketIcon}
      title={isCreate ? 'สร้าง Ticket'
        : patient ? <PatientLink patientId={patient.id} icon>{patient.name || '—'}</PatientLink>
          : 'ไม่ระบุคนไข้'}
      subtitle={isCreate ? 'ติดตามงาน/เรื่องที่ต้องดำเนินการ' : hasPatientHeader ? (
        <span className="block">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {patient.hn ? <span className="text-[12px] text-slate-400">HN {patient.hn}</span> : null}
            {pStatus ? (
              <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] ring-1 ${pTone.pill}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${pTone.dot}`} />{pStatus}
              </span>
            ) : null}
            <button type="button" onClick={() => setChangingPatient((v) => !v)}
              className="text-[11.5px] text-slate-400 hover:text-brand-600 underline decoration-dotted underline-offset-2">
              {changingPatient ? 'ยกเลิกเปลี่ยน' : 'เปลี่ยน'}
            </button>
          </span>
          <span className="block text-[11.5px] text-slate-400 mt-1">{statusLine}</span>
        </span>
      ) : <span className="text-[12px]">{statusLine}</span>}
      busy={busy}
      dirty={dirty}
      record={!isCreate && row ? row : undefined}
      recordId={!isCreate && row ? tId(row) : undefined}
      recordTable="ticket"
      footer={footer}
    >
      <div className="space-y-4">
          {/* patient swap — only when asked for, so the name isn't shown twice */}
          {hasPatientHeader && changingPatient && (
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <PatientPicker onPick={(p) => { setPatient(p); setChangingPatient(false) }} />
              {patient && (
                <button type="button" onClick={() => { setPatient(null); setChangingPatient(false) }}
                  className="mt-1.5 text-[11.5px] text-slate-400 hover:text-rose-600">เอาคนไข้ออกจาก ticket นี้</button>
              )}
            </div>
          )}

          {loading ? (
            <Loading />
          ) : loadFailed ? (
            <ErrorState message={err} onRetry={load} />
          ) : (
            <>
              {err && <ErrorState title={err} />}

              {/* 1 ─ CONTEXT: what this ticket came from (read-only, embedded on the row) */}
              {!isCreate && (appt || treat) ? (
                <div className="grid gap-2.5">
                  {appt ? (
                    <InfoCard icon={<CalendarClock size={14} />} title="นัดหมาย" id={tAppointmentId(row)}
                      right={<StatusPill code={appt.status} />}
                      lead={
                        <>
                          <span className="text-[15px] font-semibold text-slate-800 tabular-nums">{fmtDate(dateOf(appt.date))}</span>
                          {apptTime ? <span className="text-[12.5px] text-slate-400 tabular-nums">{apptTime}</span> : null}
                          {apptType ? <span className="rounded bg-brand-50 text-brand-700 px-1.5 py-0.5 text-[11px] font-medium">{apptType}</span> : null}
                        </>
                      }
                      meta={[
                        docName(appt.doctor) ? <DocChip key="d" doctor={appt.doctor} /> : null,
                        appt.branch_id != null ? <span key="b">{branchName(appt.branch_id)}</span> : null,
                        appt.from ? <span key="f">{appt.from}</span> : null,
                      ]}>
                      {appt.title ? <InfoRow label="หัวข้อ">{appt.title}</InfoRow> : null}
                      {appt.details ? <InfoRow label="รายละเอียด">{appt.details}</InfoRow> : null}
                      {appt.remark ? <InfoRow label="หมายเหตุ">{appt.remark}</InfoRow> : null}
                    </InfoCard>
                  ) : null}

                  {treat ? (
                    <InfoCard icon={<Stethoscope size={14} />} title="การรักษา" id={tTreatmentId(row)}
                      lead={
                        <>
                          <span className="text-[15px] font-semibold text-slate-800 tabular-nums">{fmtDate(dateOf(treat.date))}</span>
                          {treat.paymentMethod ? <span className="rounded bg-slate-100 text-slate-600 px-1.5 py-0.5 text-[11px] font-medium">{treat.paymentMethod}</span> : null}
                        </>
                      }
                      meta={[
                        docName(treat.doctor) ? <DocChip key="d" doctor={treat.doctor} /> : null,
                        treat.branch_id != null ? <span key="b">{branchName(treat.branch_id)}</span> : null,
                      ]}>
                      {treat.detail ? <InfoRow label="รายละเอียด">{treat.detail}</InfoRow> : null}
                      {treat.remark ? <InfoRow label="หมายเหตุ">{treat.remark}</InfoRow> : null}
                    </InfoCard>
                  ) : null}
                </div>
              ) : null}

              {/* 2 ─ THE TICKET ITSELF (the only editable group) */}
              <section className="rounded-xl border border-slate-200 bg-white">
                <div className="flex items-center gap-2 px-3.5 py-2 border-b border-slate-100">
                  <TicketIcon size={14} className="text-slate-400" />
                  <span className="text-[12.5px] font-medium text-slate-600">ข้อมูล Ticket</span>
                </div>
                <div className="px-3.5 py-3 space-y-3">
                  {isCreate && (
                    <Field label="คนไข้ (ถ้ามี)">
                      {patient ? (
                        <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
                          <span className="w-7 h-7 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-[12px] font-semibold shrink-0">{nameInitial(patient.name || '?')}</span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[13.5px] text-slate-800 truncate">{patient.name || '—'}</span>
                            {patient.hn ? <span className="block text-[11.5px] text-slate-400">HN {patient.hn}</span> : null}
                          </span>
                          <IconButton size="sm" icon={X} label="เอาออก" variant="danger-soft" onClick={() => setPatient(null)} />
                        </div>
                      ) : <PatientPicker onPick={setPatient} />}
                    </Field>
                  )}

                  <Field label="รายละเอียด" required>
                    <Textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={3}
                      placeholder="อธิบายงาน/เรื่องที่ต้องติดตาม…" className="resize-y" />
                  </Field>

                  <Field label="Tag">
                    <TagPicker value={tags} onChange={setTags} catalog={tagOptions} placeholder="เลือกแท็ก…" />
                  </Field>

                  {/* วันติดตาม + presets. An overdue ticket must be rescheduled before
                      บันทึก — but NOT before เสร็จแล้ว (closing needs no future date). */}
                  <Field label="วันติดตาม" hint={`ตั้งได้ตั้งแต่วันนี้ถึง ${fmtDate(maxFollowup)}`} error={followupError() || null}>
                    <div>
                    {mustReschedule && (
                      <Banner tone="error" className="mb-1.5">
                        เลยกำหนดติดตามมาแล้ว {overdueDays} วัน — กรุณาตั้งวันติดตามใหม่ก่อนบันทึก (หรือกด “เสร็จแล้ว” ถ้าจบงาน)
                      </Banner>
                    )}
                    <Input type="date" value={dateFollowup} min={minFollowup} max={maxFollowup}
                      onChange={(e) => pickFollowup(e.target.value)}
                      invalid={mustReschedule || !!followupError()} />
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {PRESETS.map((p) => {
                        const iso = p.iso(today)
                        const on = dateFollowup === iso
                        return (
                          <button key={p.label} type="button" onClick={() => pickFollowup(iso)} title={fmtDate(iso)}
                            className={`rounded-lg border px-2 py-1 text-[11.5px] font-medium transition ${
                              on ? 'border-brand-400 bg-brand-50 text-brand-700' : 'border-slate-200 bg-white text-slate-600 hover:border-brand-300 hover:text-brand-600'
                            }`}>
                            {p.label}
                          </button>
                        )
                      })}
                    </div>
                    </div>
                  </Field>
                </div>
              </section>

              {/* 3 ─ follow-up thread */}
              {!isCreate && (
                <section className="rounded-xl border border-slate-200 bg-white px-3.5 py-3">
                  <FollowUpThread ticketId={ticketId} entries={followUps} onChanged={() => { load(); onSaved?.() }} />
                </section>
              )}

              {/* The record's own line (#id · สร้าง · แก้ไข) is pinned bottom-left by the
                  Drawer. How long the ticket has been alive is a fact ABOUT the ticket
                  rather than provenance, so it stays here. */}
              {!isCreate && row && tAgeDays(row) != null ? (
                <div className="px-1 text-[11.5px] text-slate-400">
                  {done ? 'ใช้เวลา' : 'เปิดมาแล้ว'} {fmtDuration(tAgeDays(row))}
                </div>
              ) : null}
            </>
          )}
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title="ลบ ticket นี้?"
          message={`Ticket #${ticketId}${firstLine ? ` “${firstLine}”` : ''}${followUps.length ? ` และโน้ตติดตาม ${followUps.length} รายการ` : ''} จะถูกลบ — ย้อนกลับไม่ได้`}
          confirmLabel="ลบ Ticket" tone="danger" busy={saving}
          onConfirm={doDelete} onCancel={() => setConfirmDelete(false)}
        />
      )}
    </Drawer>
  )
}

/* ── read-only context card for the joined appointment / treatment ──────────────
 * Three tiers so the eye lands on the important part first:
 *   lead  — the headline facts (date, time, type) at a glance
 *   meta  — supporting chips on one muted line (doctor · branch · source)
 *   rows  — free-text (title/details/remark), only rendered when present
 */
function InfoCard({ icon, title, id, right, lead, meta, children }) {
  const metaItems = (meta || []).filter(Boolean)
  const hasRows = Array.isArray(children) ? children.some(Boolean) : !!children
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center gap-1.5 px-3.5 py-1.5 bg-slate-50/70 border-b border-slate-100">
        <span className="text-slate-400">{icon}</span>
        <span className="text-[11.5px] font-medium text-slate-500">{title}</span>
        {id ? <span className="text-[10.5px] text-slate-300 tabular-nums">#{id}</span> : null}
        <span className="ml-auto">{right}</span>
      </div>

      <div className="px-3.5 py-2.5">
        {lead ? <div className="flex flex-wrap items-center gap-2">{lead}</div> : null}
        {metaItems.length ? (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5 text-[12px] text-slate-500">
            {metaItems.map((m, i) => (
              <span key={i} className="inline-flex items-center gap-2">
                {i > 0 ? <span className="text-slate-300">·</span> : null}
                {m}
              </span>
            ))}
          </div>
        ) : null}
        {hasRows ? <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">{children}</div> : null}
      </div>
    </div>
  )
}

function InfoRow({ label, children }) {
  return (
    <div className="flex gap-2 text-[12.5px]">
      <span className="w-[64px] shrink-0 text-slate-400">{label}</span>
      <span className="min-w-0 flex-1 text-slate-700 whitespace-pre-wrap break-words">{children}</span>
    </div>
  )
}

function StatusPill({ code }) {
  const s = apptStatusInfo(code)
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${s.pill}`} title={`สถานะนัด: ${code || '—'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  )
}

function DocChip({ doctor }) {
  const name = docName(doctor)
  const img = docImg(doctor)
  if (!name) return <span className="text-slate-300">—</span>
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0" title={doctor?.name_eng || name}>
      {img
        ? <img src={img} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
        : <span className="w-5 h-5 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-[10px] font-semibold shrink-0">{nameInitial(name)}</span>}
      <span className="truncate">{name}</span>
    </span>
  )
}

/* ── follow-up notes thread ──────────────────────────────────────────────────
 * Each entry stores its author as a bare Employee id (`created_by`), and the
 * embedded object is NOT joined by Xano — so the code is resolved here through
 * /api/employees/:id (cachedGet dedupes across entries and across drawers).
 * Same approach as RecallPopup's note thread.
 *
 * Editing is confined to your OWN notes: the server's `edit` branch rewrites
 * details/modified_by/modified_at and preserves created_by, so an edited note
 * keeps its original author — the byline never lies about who wrote it.
 */
function FollowUpThread({ ticketId, entries, onChanged }) {
  const me = useMe()
  const meId = me?.employee_id
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [err, setErr] = useState('')
  const [authors, setAuthors] = useState({})   // employee_id → record (for the code)
  const [editId, setEditId] = useState(null)   // uuid being edited
  const [editText, setEditText] = useState('')
  const [confirmDel, setConfirmDel] = useState(null)   // the entry about to be deleted

  useEffect(() => {
    const ids = [...new Set(entries.map(fuCreatorId).filter((v) => v != null))]
    if (!ids.length) return
    let alive = true
    Promise.all(ids.map((id) => cachedGet(`/api/employees/${id}`).then((e) => [id, e]).catch(() => [id, null])))
      .then((pairs) => { if (alive) setAuthors(Object.fromEntries(pairs)) })
    return () => { alive = false }
  }, [entries])

  // Prefer an expanded object if Xano ever sends one; otherwise the resolved
  // record. `#id` is the last resort so an unresolvable author still shows that
  // SOMEONE wrote it rather than reading as anonymous.
  const creatorCode = (f) => {
    const inline = fuCreator(f)
    if (inline) return inline
    const id = fuCreatorId(f)
    if (id == null) return ''
    const e = authors[id]
    return e?.code || e?.nickname || e?.name || `#${id}`
  }

  async function add() {
    const details = text.trim()
    if (!details || !ticketId) return
    setSending(true); setErr('')
    try {
      // documented spec: { id: ticketId, mode: 'add', details }
      await postJson('/api/ticket/follow-up', { id: ticketId, mode: 'add', details })
      setText('')
      onChanged?.()
    } catch (e) {
      setErr(String(e.message || e))
    } finally {
      setSending(false)
    }
  }

  async function saveEdit(uuid) {
    const details = editText.trim()
    if (!details || !uuid || !ticketId) return
    setSending(true); setErr('')
    try {
      await postJson('/api/ticket/follow-up', { id: ticketId, mode: 'edit', uuid, details })
      setEditId(null); setEditText('')
      onChanged?.()
    } catch (e) {
      setErr(String(e.message || e))
    } finally {
      setSending(false)
    }
  }

  async function remove(uuid) {
    if (!uuid || !ticketId) return
    setSending(true); setErr('')
    try {
      await postJson('/api/ticket/follow-up', { id: ticketId, mode: 'delete', uuid })
      onChanged?.()
    } catch (e) {
      setErr(String(e.message || e))
    } finally {
      setSending(false)
    }
  }

  return (
    <div>
      <div className="text-[12.5px] font-medium text-slate-600 mb-1 flex items-center gap-1.5"><MessageSquarePlus size={13} /> การติดตาม {entries.length > 0 && <span className="text-slate-400">({entries.length})</span>}</div>

      {entries.length > 0 && (
        <div className="space-y-2 mb-2.5">
          {[...entries].sort((a, b) => (fuDate(b) > fuDate(a) ? 1 : -1)).map((f, i) => {
            const uuid = fuId(f)
            const authorId = fuCreatorId(f)
            const code = creatorCode(f)
            // Only the author may edit. An entry with no resolvable author (legacy
            // rows written before created_by was stamped) belongs to nobody, so it
            // stays read-only rather than becoming editable by everyone.
            const mine = meId != null && authorId != null && String(authorId) === String(meId)
            const editing = editId && uuid && editId === uuid
            const editedAt = fuEditedAt(f)
            return (
              <div key={uuid ?? i} className="group rounded-xl border border-slate-200 bg-white px-3 py-2">
                {editing ? (
                  <div className="flex items-end gap-2">
                    <Textarea autoFocus value={editText} onChange={(e) => setEditText(e.target.value)} rows={2}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveEdit(uuid)
                        // Esc cancels the inline edit only — it must not also reach the Drawer.
                        if (e.key === 'Escape') { e.stopPropagation(); setEditId(null); setEditText('') }
                      }}
                      className="resize-y flex-1" />
                    <div className="flex items-center gap-1 pb-1">
                      <IconButton size="sm" icon={Check} label="บันทึก (⌘/Ctrl+Enter)" variant="soft"
                        onClick={() => saveEdit(uuid)} disabled={sending || !editText.trim()} />
                      <IconButton size="sm" icon={X} label="ยกเลิก (Esc)" onClick={() => { setEditId(null); setEditText('') }} />
                    </div>
                  </div>
                ) : (
                  <div className="text-[13px] text-slate-700 whitespace-pre-wrap break-words">{fuDetails(f) || '—'}</div>
                )}
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[11px] text-slate-400">
                    {fmtDate(fuDate(f))}
                    {code ? <> · <span className="font-medium text-slate-500">{code}</span></> : null}
                    {editedAt ? <span className="text-slate-300"> · แก้ไข {fmtDate(editedAt)}</span> : null}
                  </span>
                  {uuid && !editing && (
                    <span className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
                      <IconButton size="sm" icon={Pencil} label="แก้ไขโน้ตนี้" disabled={sending || !mine}
                        disabledReason={mine ? undefined : 'แก้ไขได้เฉพาะโน้ตที่คุณบันทึก'}
                        onClick={() => { setEditId(uuid); setEditText(fuDetails(f)) }} />
                      <IconButton size="sm" icon={Trash2} label="ลบโน้ตนี้" variant="danger-soft" disabled={sending}
                        onClick={() => setConfirmDel(f)} />
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {err && <div className="mb-2 text-[12px] text-rose-600">{err}</div>}
      <div className="flex items-end gap-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) add() }}
          placeholder="เพิ่มโน้ตติดตาม… (⌘/Ctrl+Enter)" className="resize-y flex-1" />
        <Button icon={Send} loading={sending} disabled={!text.trim()} onClick={add} aria-label="เพิ่มโน้ตติดตาม" title="เพิ่มโน้ตติดตาม" className="shrink-0" />
      </div>

      {/* Deleting a follow-up note always confirms and names the note (house rule). */}
      {confirmDel && (
        <ConfirmDialog
          tone="danger"
          title="ลบโน้ตติดตามนี้?"
          message={`“${(String(fuDetails(confirmDel) || '').split('\n').map((x) => x.trim()).find(Boolean) || '(ไม่มีข้อความ)').slice(0, 120)}” จะถูกลบออกจาก ticket นี้`}
          confirmLabel="ลบโน้ต"
          busy={sending}
          // remove() never throws (it reports into `err`), so the dialog closes either way.
          onConfirm={async () => { await remove(fuId(confirmDel)); setConfirmDel(null) }}
          onCancel={() => setConfirmDel(null)}
        />
      )}
    </div>
  )
}

/* ── debounced patient search → dropdown ───────────────────────────────────── */
function PatientPicker({ onPick }) {
  const [q, setQ] = useState('')
  const [rows, setRows] = useState([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const reqId = useRef(0)

  useEffect(() => {
    const term = q.trim()
    if (term.length < MIN_PATIENT_SEARCH) { setRows([]); setLoading(false); setOpen(false); return }
    const id = ++reqId.current
    setLoading(true)
    const t = setTimeout(() => {
      apiGet('/api/patients', { search: term, per_page: 8 })
        .then((r) => {
          if (id !== reqId.current) return
          const items = Array.isArray(r) ? r : (r?.items || [])
          setRows(items); setOpen(true)
        })
        .catch(() => { if (id === reqId.current) setRows([]) })
        .finally(() => { if (id === reqId.current) setLoading(false) })
    }, 300)
    return () => clearTimeout(t)
  }, [q])

  function pick(p) {
    const name = [p.prefix, p.name, p.surname].filter(Boolean).join(' ').trim() || p.fullname || '—'
    onPick({ id: p.id ?? p.patient_id, name, hn: p.hn || p.HN || '' })
    setQ(''); setRows([]); setOpen(false)
  }

  const ta = useTypeahead({
    count: rows.length,
    onSelect: (i) => pick(rows[i]),
    onClose: () => setOpen(false),
  })

  return (
    <div className="relative">
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => rows.length && setOpen(true)} onKeyDown={ta.onKeyDown}
          placeholder="ค้นหาชื่อ · HN · เบอร์…" className={`${FIELD_CLASS} pl-8`} />
        {loading && <Loader2 size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
      </div>
      {open && rows.length > 0 && (
        <>
          <button className="fixed inset-0 z-10 cursor-default" onClick={() => setOpen(false)} aria-hidden tabIndex={-1} />
          <div ref={ta.listRef} className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg py-1">
            {rows.map((p, i) => {
              const name = [p.prefix, p.name, p.surname].filter(Boolean).join(' ').trim() || p.fullname || '—'
              const hn = p.hn || p.HN || ''
              return (
                <button type="button" key={p.id ?? p.patient_id} data-ti-index={i} onMouseEnter={() => ta.setActiveIndex(i)} onClick={() => pick(p)}
                  className={`flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50 ${i === ta.activeIndex ? 'bg-slate-100' : ''}`}>
                  <span className="w-7 h-7 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-[12px] font-semibold shrink-0">{nameInitial(name)}</span>
                  <span className="min-w-0">
                    <span className="block text-[13.5px] text-slate-800 truncate">{name}</span>
                    {hn ? <span className="block text-[11.5px] text-slate-400">HN {hn}{p.phone ? ` · ${p.phone}` : ''}</span> : null}
                  </span>
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
